// src/components/MobileFooterNav.js
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import { Home, Search, Plus, MessageCircle, LayoutGrid } from "lucide-react";

/* =============== STYLES =============== */
const TabBar = styled.div`
  display: none;

  /* Only show in Capacitor native app */
  body.capacitor & {
    @media (max-width: 768px) {
      display: block;
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10000;
      background: #ffffff;
      border-top: 0.5px solid rgba(0, 0, 0, 0.1);
      padding-bottom: env(safe-area-inset-bottom);
    }
  }
`;

const TabList = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 64px;
  padding: 10px 8px 32px 8px;
`;

const TabItem = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px 0 4px 0;
  position: relative;
  -webkit-tap-highlight-color: transparent;

  &:active {
    opacity: 0.5;
  }

  ${props => props.$isCenter && css`
    flex: 1;
  `}
`;

const IconWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: #0f172a;
    stroke-width: ${props => props.$active ? 2 : 1.5};
  }
`;

const PostButtonWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 2.5px solid #0f172a;
  border-radius: 8px;

  svg {
    color: #0f172a;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -6px;
  right: -10px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 600;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* =============== COMPONENT =============== */
const MobileFooterNavigation = ({ unreadCount }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (paths) => {
    if (Array.isArray(paths)) {
      return paths.some(p =>
        p === "/"
          ? location.pathname === "/" || location.pathname === "/home"
          : location.pathname === p || location.pathname.startsWith(p + "/")
      );
    }
    if (paths === "/") return location.pathname === "/" || location.pathname === "/home";
    return location.pathname === paths || location.pathname.startsWith(paths + "/");
  };

  const go = (path) => () => navigate(path);

  const tabs = [
    {
      id: "home",
      icon: Home,
      paths: ["/", "/home"],
      onClick: go("/"),
    },
    {
      id: "browse",
      icon: Search,
      paths: ["/tasks"],
      onClick: go("/tasks"),
    },
    {
      id: "post",
      isCenter: true,
      onClick: go("/posttask"),
    },
    {
      id: "messages",
      icon: MessageCircle,
      paths: ["/chat"],
      onClick: go("/chat"),
      badge: unreadCount > 0 ? (unreadCount > 99 ? "99+" : unreadCount) : null,
    },
    {
      id: "dashboard",
      icon: LayoutGrid,
      paths: ["/dashboard", "/usertasks", "/account"],
      onClick: go("/dashboard"),
    },
  ];

  return (
    <TabBar>
      <TabList aria-label="Main navigation">
        {tabs.map((tab) => {
          if (tab.isCenter) {
            return (
              <TabItem key={tab.id} $isCenter onClick={tab.onClick} aria-label="Post a task">
                <PostButtonWrap>
                  <Plus size={20} strokeWidth={2.5} />
                </PostButtonWrap>
              </TabItem>
            );
          }

          const Icon = tab.icon;
          const active = isActive(tab.paths);

          return (
            <TabItem key={tab.id} onClick={tab.onClick} aria-label={tab.id}>
              <IconWrap $active={active}>
                <Icon size={26} strokeWidth={active ? 2.5 : 1.5} />
                {tab.badge && <Badge>{tab.badge}</Badge>}
              </IconWrap>
            </TabItem>
          );
        })}
      </TabList>
    </TabBar>
  );
};

const mapStateToProps = (state) => ({
  unreadCount: state.user?.unread_message_count || 0,
});

export default connect(mapStateToProps)(MobileFooterNavigation);
